import React, { useCallback, useEffect, useRef } from 'react'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { MediaCard } from '@/molecules/MediaCard'
import {
  BrandLandingTabItemInteraction,
  useContentInteraction,
  HOVER_THRESHOLD_MS,
} from '@/services/Analytics/ContentInteractions'
import { ProjectType } from '@/services/ProjectsService'
import { Media } from '@/utils/EpisodeUtil'

export interface EpisodeCardProps extends Omit<Media, 'projectType'> {
  itemIndex: number
  pagePlacement: number
  className?: string
  thumbnailClassName?: string
  imgPriority?: boolean
  includeHoverScale?: boolean
  userHasEarlyAccess?: boolean
  hasWatchableReasons?: boolean
  projectType?: ProjectType | string | null
  theaterEpisodeCard?: boolean
}

export const EpisodeCard: React.FC<EpisodeCardProps> = ({
  itemIndex,
  pagePlacement,
  className,
  thumbnailClassName,
  imgPriority,
  includeHoverScale,
  userHasEarlyAccess,
  hasWatchableReasons,
  projectType,
  theaterEpisodeCard,
  ...media
}) => {
  const { trackInteraction } = useContentInteraction()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9, // Track when at least 90% of the card is visible
  })

  // Get media ID and thumbnail path from available properties
  const mediaContentId = media.contentEpisodeId || media.guid || ''
  const mediaThumbnailPath = media.thumbnailPath || media.poster

  // Hover tracking state
  const hoverStartTimeRef = useRef<number | null>(null)
  const impressionTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hasTrackedImpression = useRef(false)

  const handleMouseEnter = useCallback(() => {
    if (!mediaContentId) return

    // Record the start time
    hoverStartTimeRef.current = Date.now()
  }, [mediaContentId])

  const handleMouseLeave = useCallback(() => {
    if (!mediaContentId || !hoverStartTimeRef.current) return

    const dwellTime = Date.now() - hoverStartTimeRef.current

    // Only track hover if the user hovered for at least the threshold time
    if (dwellTime >= HOVER_THRESHOLD_MS && mediaThumbnailPath) {
      const interaction = new BrandLandingTabItemInteraction({
        id: mediaContentId,
        __typename: 'ContentEpisode',
        attribution: {
          itemIndex,
          pagePlacement,
          image: {
            cloudinaryPath: mediaThumbnailPath,
          },
        },
      })
        .withSurface('brand_landing')
        .withPlacement('episodes_tab', pagePlacement, itemIndex, 'vertical_list')
        .withImageArtwork(mediaThumbnailPath, 'landscape_display')
        .withHover(dwellTime)

      trackInteraction(interaction)
    }

    // Reset state
    hoverStartTimeRef.current = null
  }, [mediaContentId, mediaThumbnailPath, pagePlacement, itemIndex, trackInteraction])

  const handleClick = useCallback(() => {
    if (!mediaContentId || !mediaThumbnailPath) return

    const interaction = new BrandLandingTabItemInteraction({
      id: mediaContentId,
      __typename: 'ContentEpisode',
      attribution: {
        itemIndex,
        pagePlacement,
        image: {
          cloudinaryPath: mediaThumbnailPath,
        },
      },
    })
      .withSurface('brand_landing')
      .withPlacement('episodes_tab', pagePlacement, itemIndex, 'vertical_list')
      .withImageArtwork(mediaThumbnailPath, 'landscape_display')
      .withPress()

    trackInteraction(interaction)
  }, [mediaContentId, mediaThumbnailPath, pagePlacement, itemIndex, trackInteraction])

  // Track impression when the card becomes visible for at least 1 second
  useEffect(() => {
    if (!mediaContentId || hasTrackedImpression.current) return

    if (isVisible) {
      // Start a timer to track impression after 1 second
      impressionTimeoutRef.current = setTimeout(() => {
        hasTrackedImpression.current = true

        if (mediaThumbnailPath) {
          const interaction = new BrandLandingTabItemInteraction({
            id: mediaContentId,
            __typename: 'ContentEpisode',
            attribution: {
              itemIndex,
              pagePlacement,
              image: {
                cloudinaryPath: mediaThumbnailPath,
              },
            },
          })
            .withSurface('brand_landing')
            .withPlacement('episodes_tab', pagePlacement, itemIndex, 'vertical_list')
            .withImageArtwork(mediaThumbnailPath, 'landscape_display')
            .withImpression()

          trackInteraction(interaction)
        }
      }, 1000) // Wait 1 second before tracking impression
    } else {
      // Clear timeout if item becomes not visible before 1 second
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
        impressionTimeoutRef.current = null
      }
    }

    return () => {
      if (impressionTimeoutRef.current) {
        clearTimeout(impressionTimeoutRef.current)
      }
    }
  }, [isVisible, mediaContentId, mediaThumbnailPath, trackInteraction, itemIndex, pagePlacement])

  return (
    <div ref={cardRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>
      <MediaCard
        className={className}
        thumbnailClassName={thumbnailClassName}
        {...media}
        imgPriority={imgPriority}
        includeHoverScale={includeHoverScale}
        userHasEarlyAccess={userHasEarlyAccess}
        hasWatchableReasons={hasWatchableReasons}
        watchableAvailabilityStatus={media?.watchableAvailabilityStatus ?? null}
        projectType={projectType as ProjectType | undefined}
        theaterEpisodeCard={theaterEpisodeCard}
      />
    </div>
  )
}
